import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff, KeyRound, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Forced password change modal — shown when a user logs in with a temp password
 * (must_change_password=true on their record). Cannot be dismissed until the
 * password is successfully changed.
 */
const ForcePasswordChange = ({ user, onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    if (form.new_password === form.current_password) {
      setError('New password must be different from your temporary password');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: form.current_password,
          new_password: form.new_password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');
      setSuccess(true);
      setTimeout(() => { onSuccess && onSuccess(); }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="force-password-change-modal"
    >
      <div className="bg-[#0f0f0f] border border-amber-500/40 rounded-2xl shadow-2xl max-w-md w-full p-7">
        {success ? (
          <div className="text-center py-4" data-testid="force-change-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">All set!</h2>
            <p className="text-gray-400">Your password has been updated. Welcome to Life Decoder.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Set a new password</h2>
                <p className="text-xs text-gray-400">Required before continuing</p>
              </div>
            </div>

            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Hi {user?.name?.split(' ')[0] || 'there'}, you're logged in with a temporary password. For your account security, please choose a new password to continue.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center" data-testid="force-change-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="force-password-change-form">
              <div>
                <Label htmlFor="current_password" className="text-gray-300">Temporary Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="The one you received"
                    value={form.current_password}
                    onChange={handleChange}
                    required
                    autoFocus
                    data-testid="force-current-password"
                    className="pl-9 bg-[#1a1a1a] border-gray-800 text-white placeholder:text-gray-600 focus:border-amber-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-gray-300">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 6 characters"
                    value={form.new_password}
                    onChange={handleChange}
                    required
                    data-testid="force-new-password"
                    className="pl-9 pr-9 bg-[#1a1a1a] border-gray-800 text-white placeholder:text-gray-600 focus:border-amber-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-gray-300">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter new password"
                    value={form.confirm_password}
                    onChange={handleChange}
                    required
                    data-testid="force-confirm-password"
                    className="pl-9 bg-[#1a1a1a] border-gray-800 text-white placeholder:text-gray-600 focus:border-amber-500"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="force-change-submit"
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-5"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full mr-2" />
                    Updating...
                  </div>
                ) : (
                  'Update Password'
                )}
              </Button>

              <p className="text-[11px] text-gray-500 text-center">
                You won't be able to access the platform until this is done.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForcePasswordChange;
